import apiClient from '../config/api';
import { DocumentCustomization, LogoUploadResponse } from '../types/documentCustomization.types';

const BASE_URL = '/api/customization';

class DocumentCustomizationService {
  /**
   * Récupérer la personnalisation de l'utilisateur connecté
   */
  async getCustomization(): Promise<DocumentCustomization> {
    try {
      console.log('📋 Récupération de la personnalisation...');
      const response = await apiClient.get<DocumentCustomization>(BASE_URL);
      console.log('✅ Personnalisation récupérée:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors de la récupération:', error);
      throw error;
    }
  }

  /**
   * Créer ou mettre à jour la personnalisation
   */
  async updateCustomization(data: Partial<DocumentCustomization>): Promise<DocumentCustomization> {
    try {
      console.log('💾 Mise à jour de la personnalisation:', data);
      const response = await apiClient.put<DocumentCustomization>(BASE_URL, data, {
        timeout: 30000, // 30 secondes pour les mises à jour
      });
      console.log('✅ Personnalisation mise à jour avec succès');
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors de la mise à jour:', error);

      // Déterminer le type d'erreur pour un message approprié
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('La requête a pris trop de temps. Veuillez vérifier que le serveur backend est accessible et réessayer.');
      } else if (error.response) {
        throw new Error(error.response.data?.message || 'Erreur du serveur lors de la mise à jour');
      } else if (error.request) {
        throw new Error('Impossible de contacter le serveur. Vérifiez que le backend est démarré.');
      } else {
        throw new Error('Erreur lors de la mise à jour de la personnalisation');
      }
    }
  }

  /**
   * Uploader un logo
   */
  async uploadLogo(file: File): Promise<LogoUploadResponse> {
    try {
      console.log('📤 Upload du logo:', file.name);

      // Validation côté client
      if (!this.validateLogoFile(file)) {
        throw new Error('Format de fichier invalide ou taille trop importante');
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post<LogoUploadResponse>(
        `${BASE_URL}/logo`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 60000, // 60 secondes pour l'upload de fichiers
        }
      );

      console.log('✅ Logo uploadé avec succès:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors de l\'upload du logo:', error);

      // Déterminer le type d'erreur pour un message approprié
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('L\'upload du logo a pris trop de temps. Le fichier est peut-être trop volumineux ou votre connexion est lente.');
      } else if (error.response) {
        throw new Error(error.response.data?.message || 'Erreur du serveur lors de l\'upload du logo');
      } else if (error.request) {
        throw new Error('Impossible de contacter le serveur. Vérifiez que le backend est démarré.');
      } else {
        throw new Error('Erreur lors de l\'upload du logo');
      }
    }
  }

  /**
   * Supprimer le logo
   */
  async deleteLogo(): Promise<void> {
    try {
      console.log('🗑️ Suppression du logo...');
      await apiClient.delete(`${BASE_URL}/logo`);
      console.log('✅ Logo supprimé avec succès');
    } catch (error: any) {
      console.error('❌ Erreur lors de la suppression du logo:', error);
      throw error;
    }
  }

  /**
   * Réinitialiser aux valeurs par défaut
   */
  async resetToDefault(): Promise<DocumentCustomization> {
    try {
      console.log('🔄 Réinitialisation...');
      const response = await apiClient.post<DocumentCustomization>(`${BASE_URL}/reset`);
      console.log('✅ Personnalisation réinitialisée');
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors de la réinitialisation:', error);
      throw error;
    }
  }

  /**
   * Générer un PDF de test
   */
  async generateTestPDF(type: 'facture' | 'devis'): Promise<Blob> {
    try {
      console.log(`🧪 Génération d'un PDF de test (${type})...`);
      const response = await apiClient.get(`${BASE_URL}/test-pdf/${type}`, {
        responseType: 'blob',
        timeout: 30000, // 30 secondes pour la génération de PDF
      });
      console.log('✅ PDF de test généré');
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors de la génération du PDF:', error);
      throw error;
    }
  }

  /**
   * Valider un fichier logo
   */
  private validateLogoFile(file: File): boolean {
    // Formats acceptés
    const acceptedFormats = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!acceptedFormats.includes(file.type)) {
      console.error('Format non accepté:', file.type);
      return false;
    }

    // Taille max 2MB
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      console.error('Fichier trop volumineux:', file.size, 'bytes');
      return false;
    }

    return true;
  }

  /**
   * Valider une couleur hexadécimale
   */
  validateHexColor(color: string): boolean {
    return /^#[0-9A-F]{6}$/i.test(color);
  }

  /**
   * Formater une couleur (ajouter # si manquant)
   */
  formatHexColor(color: string): string {
    return color.startsWith('#') ? color : `#${color}`;
  }
}

export default new DocumentCustomizationService();
